import React, { useState, useEffect, useContext } from "react";
import * as XLSX from "xlsx";
import { StickyScrollWrapper } from "../components/StickyScrollWrapper";
import ZenPagination from "../components/ZenPagination";

function resizeImageToBase64(file, maxPx = 400, quality = 0.88) {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const scale = Math.min(1, maxPx / Math.max(img.width, img.height));
        const canvas = document.createElement("canvas");
        canvas.width = img.width * scale; canvas.height = img.height * scale;
        canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL("image/jpeg", quality));
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  });
}
import {
  LayoutDashboard, ShoppingCart, Package, Users, Wallet, Layers,
  Sun, Moon, Globe, LogOut, Plus, Search, Printer, Truck, DollarSign,
  X, CheckCircle2, Send, Trash2, Pencil, Filter, FileDown, ChevronRight, ChevronDown, ChevronLeft,
  AlertTriangle, TrendingUp, Boxes, Receipt, Settings, MessageCircle,
  Sparkles, Grid, Ruler, FileText, Factory, Upload, Bell, CreditCard, ClipboardList, RefreshCw,
  Home, ChevronsLeft, ChevronsRight, Mail, Share2, Eye, EyeOff, Check, CalendarDays, Camera, FileSignature, Target, MapPin, Tag, Palette, AlertCircle, Download,
  Brain, Zap, Cpu, Info, Building2, History, Phone, Shield, BarChart2, Bot, Radio, Copy, Barcode, PlusCircle, ShieldAlert, ExternalLink, Loader2, Image as ImageIcon,
} from "lucide-react";
import { toast } from "sonner";
import api from "../api";
import { AuthCtx, UICtx, StockSettingsCtx } from "../appContexts";
import { fmt, fmtDate, fmtDT, ono, MONEY, convertPrice } from "../appUtils";
import { translations, CURRENCY_SYMBOL, STATUS_LABELS } from "../mock";
import { ModuleIcon, PName, MODULE_GRADIENTS } from "../components/layout/Sidebar";
import SupplierModule from "../components/SupplierModule";
import { StatementDialog } from "./CustomersPage";

function AccountingModule() {
  const [tab, setTab] = useState("tahsilat"); // tahsilat | kasa | tedarik
  return (
    <div className="space-y-4 zen-anim-up">
      <div className="flex items-center gap-3">
        <ModuleIcon tab="accounting" icon={Wallet} />
        <h2 className="text-2xl font-extrabold tracking-tight">Muhasebe / Ödemeler</h2>
      </div>
      <div className="flex gap-2 border-b border-slate-200 dark:border-[#22304A]">
        <button
          data-testid="acc-tab-tahsilat"
          onClick={() => setTab("tahsilat")}
          className={`px-4 py-2 text-sm font-semibold border-b-2 transition-colors ${
            tab === "tahsilat"
              ? "border-[#E5322A] text-[#E5322A]"
              : "border-transparent text-slate-500 hover:text-slate-800 dark:hover:text-slate-100"
          }`}
        >Tahsilat &amp; Ödemeler</button>
        <button
          data-testid="acc-tab-kasa"
          onClick={() => setTab("kasa")}
          className={`px-4 py-2 text-sm font-semibold border-b-2 transition-colors ${
            tab === "kasa"
              ? "border-[#E5322A] text-[#E5322A]"
              : "border-transparent text-slate-500 hover:text-slate-800 dark:hover:text-slate-100"
          }`}
        >Kasa &amp; Banka &amp; Çek Portföyü</button>
        <button
          data-testid="acc-tab-tedarik"
          onClick={() => setTab("tedarik")}
          className={`px-4 py-2 text-sm font-semibold border-b-2 transition-colors flex items-center gap-1.5 ${
            tab === "tedarik"
              ? "border-violet-500 text-violet-600 dark:text-violet-400"
              : "border-transparent text-slate-500 hover:text-slate-800 dark:hover:text-slate-100"
          }`}
        >
          <Building2 className="w-3.5 h-3.5" />
          Tedarikçiler
        </button>
      </div>
      {tab === "tahsilat" && <TahsilatOdemeModule />}
      {tab === "kasa" && <KasaBankaModule />}
      {tab === "tedarik" && <SupplierModule />}
    </div>
  );
}

function KasaBankaModule() {
  const [accounts, setAccounts] = useState([]);
  const [selected, setSelected] = useState(null);
  const [movements, setMovements] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    const [a, c] = await Promise.all([api.get("/cash-accounts"), api.get("/customers")]);
    setAccounts(a.data); setCustomers(c.data);
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (!selected) { setMovements([]); return; }
    (async () => {
      const { data } = await api.get(`/cash-accounts/${selected.id}/movements`);
      setMovements(data);
    })();
  }, [selected]);

  const save = async (d) => {
    try {
      if (editing?.id) await api.put(`/cash-accounts/${editing.id}`, d);
      else await api.post("/cash-accounts", d);
      toast.success("Kaydedildi"); setEditing(null); load();
    } catch (e) { toast.error("Kaydedilemedi"); }
  };
  const del = async (a) => {
    if (!window.confirm(`${a.name} silinsin mi? Bağlı ödemeler etkilenmez.`)) return;
    await api.delete(`/cash-accounts/${a.id}`); toast.success("Silindi"); load(); setSelected(null);
  };

  const TYPES = [
    { id: "nakit",  label: "Nakit Kasa",   icon: DollarSign, color: "emerald" },
    { id: "banka",  label: "Banka Hesabı", icon: CreditCard, color: "sky" },
    { id: "cek",    label: "Çek Portföyü", icon: FileText,   color: "amber" },
  ];

  return (
    <div className="grid lg:grid-cols-[380px_1fr] gap-4">
      {/* Accounts list */}
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <h3 className="text-sm font-bold">Hesaplar ({accounts.length})</h3>
          <button data-testid="new-account-btn" onClick={() => setEditing({})} className="zen-btn-primary !text-xs"><Plus className="w-3.5 h-3.5" /> Yeni Hesap</button>
        </div>
        {TYPES.map(t => {
          const items = accounts.filter(a => a.type === t.id);
          const Icon = t.icon;
          return (
            <div key={t.id} className="space-y-1.5">
              <div className={`flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider text-${t.color}-500`}>
                <Icon className="w-3.5 h-3.5" /> {t.label} ({items.length})
              </div>
              {items.length === 0 && <div className="text-xs text-slate-400 italic px-3 py-2">Bu türde hesap yok</div>}
              {items.map(a => {
                const isSel = selected?.id === a.id;
                return (
                  <button
                    key={a.id}
                    data-testid={`acc-item-${a.id}`}
                    onClick={() => setSelected(a)}
                    className={`w-full text-left p-3 rounded-lg border transition-all ${
                      isSel
                        ? `border-${t.color}-500 bg-${t.color}-500/10`
                        : "border-slate-200 dark:border-[#22304A] hover:border-slate-300"
                    }`}
                  >
                    <div className="flex justify-between items-start gap-2">
                      <div className="min-w-0">
                        <div className="text-sm font-bold truncate">{a.name}</div>
                        {a.bankName && <div className="text-[10px] text-slate-500 truncate">{a.bankName}{a.iban ? ` · ${a.iban.slice(-6)}` : ""}</div>}
                      </div>
                      <div className={`text-sm font-extrabold zen-mono ${a.balance > 0 ? "text-emerald-500" : a.balance < 0 ? "text-rose-500" : ""}`}>
                        {fmt(a.balance || 0, a.currency)}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          );
        })}
      </div>

      {/* Movements */}
      <div className="zen-panel p-4">
        {!selected ? (
          <div className="p-12 text-center">
            <Wallet className="w-12 h-12 mx-auto text-slate-300 mb-3" />
            <div className="text-sm text-slate-500">Detayı görmek için soldan bir hesap seçin</div>
          </div>
        ) : (
          <>
            <div className="flex justify-between items-start mb-4">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-slate-500">{selected.type === "nakit" ? "Nakit Kasa" : selected.type === "banka" ? "Banka Hesabı" : "Çek Portföyü"}</div>
                <div className="text-xl font-extrabold">{selected.name}</div>
                {selected.bankName && <div className="text-xs text-slate-500 mt-0.5">{selected.bankName}{selected.iban ? ` · IBAN: ${selected.iban}` : ""}</div>}
              </div>
              <div className="text-right">
                <div className="text-[10px] uppercase tracking-wider text-slate-500">Güncel Bakiye</div>
                <div className={`text-2xl font-extrabold zen-mono ${selected.balance > 0 ? "text-emerald-500" : selected.balance < 0 ? "text-rose-500" : ""}`}>{fmt(selected.balance || 0, selected.currency)}</div>
              </div>
            </div>
            <div className="flex gap-2 mb-3">
              <button onClick={() => setEditing(selected)} className="zen-btn-ghost !text-xs"><Pencil className="w-3.5 h-3.5" /> Düzenle</button>
              <button onClick={() => del(selected)} className="zen-btn-ghost !text-xs !text-rose-500 border-rose-500/30"><Trash2 className="w-3.5 h-3.5" /> Sil</button>
            </div>
            <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Hareketler ({movements.length})</div>
            <div className="overflow-x-auto">
              <table className="zen-table !text-xs">
                <thead><tr><th>Tarih</th><th>Cari</th><th>Tür</th><th>Yöntem</th><th className="text-right">Giriş</th><th className="text-right">Çıkış</th><th>Not</th></tr></thead>
                <tbody>
                  {movements.length === 0 && <tr><td colSpan={7} className="text-center text-slate-500 py-6">Bu hesaba bağlı hareket yok</td></tr>}
                  {movements.map(m => {
                    const c = customers.find(x => x.id === m.customerId);
                    const isIn = m.kind === "tahsilat";
                    return (
                      <tr key={m.id}>
                        <td className="whitespace-nowrap">{m.date}</td>
                        <td>{c?.name || "-"}</td>
                        <td><span className={`zen-chip !text-[10px] ${isIn ? "bg-emerald-500/10 text-emerald-500" : "bg-rose-500/10 text-rose-500"}`}>{isIn ? "Tahsilat" : "Tediye"}</span></td>
                        <td>{m.method}{m.checkNo ? ` (${m.checkNo})` : ""}</td>
                        <td className="text-right zen-mono text-emerald-500">{isIn ? fmt(m.amount, m.currency) : ""}</td>
                        <td className="text-right zen-mono text-rose-500">{!isIn ? fmt(m.amount, m.currency) : ""}</td>
                        <td className="text-xs text-slate-500 max-w-xs truncate">{m.note || "-"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {editing !== null && <CashAccountForm data={editing} onClose={() => setEditing(null)} onSave={save} />}
    </div>
  );
}

function CashAccountForm({ data, onClose, onSave }) {
  const [f, setF] = useState({
    type: "nakit", name: "", currency: "TL", bankName: "", iban: "", balance: 0, notes: "", active: true, ...data,
  });
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="zen-panel w-full max-w-md p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-base font-bold">{data?.id ? "Hesap Düzenle" : "Yeni Kasa / Banka / Çek Hesabı"}</h3>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <div className="space-y-3">
          <div>
            <label className="block text-[10px] font-semibold uppercase text-slate-500 mb-1">Hesap Türü</label>
            <div className="grid grid-cols-3 gap-2">
              {[
                { id: "nakit", label: "Nakit Kasa", color: "emerald" },
                { id: "banka", label: "Banka Hesabı", color: "sky" },
                { id: "cek", label: "Çek Portföyü", color: "amber" },
              ].map(t => (
                <button key={t.id} type="button" onClick={() => setF({ ...f, type: t.id })}
                  data-testid={`acc-type-${t.id}`}
                  className={`p-2 rounded-lg border-2 text-xs font-semibold ${
                    f.type === t.id
                      ? `border-${t.color}-500 bg-${t.color}-500/10 text-${t.color}-600 dark:text-${t.color}-400`
                      : "border-slate-200 dark:border-[#22304A] text-slate-500"
                  }`}>{t.label}</button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-[10px] font-semibold uppercase text-slate-500 mb-1">Hesap Adı</label>
            <input data-testid="acc-name" className="zen-input" placeholder="Örn: TL Kasa Ana, Garanti EUR" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[10px] font-semibold uppercase text-slate-500 mb-1">Para Birimi</label>
              <select className="zen-input" value={f.currency} onChange={(e) => setF({ ...f, currency: e.target.value })}><option>TL</option><option>EUR</option><option>USD</option></select>
            </div>
            <div>
              <label className="block text-[10px] font-semibold uppercase text-slate-500 mb-1">Açılış Bakiye</label>
              <input type="number" step="0.01" className="zen-input !text-right" value={f.balance || 0} onChange={(e) => setF({ ...f, balance: Number(e.target.value) })} />
            </div>
          </div>
          {f.type === "banka" && (
            <>
              <div>
                <label className="block text-[10px] font-semibold uppercase text-slate-500 mb-1">Banka Adı</label>
                <input className="zen-input" placeholder="Örn: Garanti BBVA" value={f.bankName || ""} onChange={(e) => setF({ ...f, bankName: e.target.value })} />
              </div>
              <div>
                <label className="block text-[10px] font-semibold uppercase text-slate-500 mb-1">IBAN</label>
                <input className="zen-input zen-mono" placeholder="TR33 0006 ..." value={f.iban || ""} onChange={(e) => setF({ ...f, iban: e.target.value })} />
              </div>
            </>
          )}
          <div>
            <label className="block text-[10px] font-semibold uppercase text-slate-500 mb-1">Not</label>
            <textarea rows={2} className="zen-input" value={f.notes || ""} onChange={(e) => setF({ ...f, notes: e.target.value })} />
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onClose} className="zen-btn-ghost">İptal</button>
          <button data-testid="acc-save" onClick={() => {
            if (!String(f.name || "").trim()) { toast.error("Hesap adı zorunludur"); return; }
            const bal = Number(f.balance);
            if (!isFinite(bal)) { toast.error("Açılış bakiyesi sayısal olmalıdır"); return; }
            onSave({ ...f, name: f.name.trim(), balance: bal });
          }} className="zen-btn-primary">Kaydet</button>
        </div>
      </div>
    </div>
  );
}

function TahsilatOdemeModule() {
  const [payments, setPayments] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [orders, setOrders] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [viewCust, setViewCust] = useState(null);
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const load = async () => {
    const [p, c, o] = await Promise.all([api.get("/payments"), api.get("/customers"), api.get("/orders")]);
    setPayments(p.data); setCustomers(c.data); setOrders(o.data);
  };
  useEffect(() => { load(); }, []);

  const totalReceivable = customers.filter(c => c.type !== "Tedarikçi" && c.type !== "Çalışan").reduce((s, c) => s + Math.max(c.balance, 0), 0);
  const openOrders = orders.filter(o => o.status !== "odendi" && o.status !== "iptal" && o.status !== "teklif");
  const pendingCollection = openOrders.reduce((s, o) => s + ((o.total || 0) - (o.paidAmount || 0)), 0);

  const filteredPayments = payments.filter(p => {
    if (!q) return true;
    const lower = q.toLowerCase();
    const custName = (customers.find(x => x.id === p.customerId)?.name || "").toLowerCase();
    const amtStr = String(p.amount || "");
    return custName.includes(lower) || amtStr.includes(lower) || (p.note || "").toLowerCase().includes(lower);
  });
  const pagedPayments = filteredPayments.slice((page - 1) * pageSize, page * pageSize);
  useEffect(() => { setPage(1); }, [q]);

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center flex-wrap gap-2">
        <div className="flex items-center gap-2 zen-panel px-3">
          <Search className="w-4 h-4 text-slate-400" />
          <input
            data-testid="accounting-search"
            value={q} onChange={e => setQ(e.target.value)}
            placeholder="Cari adı veya tutar ara..."
            className="bg-transparent outline-none text-sm py-2 w-48"
          />
          {q && <button onClick={() => setQ("")} className="text-slate-400 hover:text-slate-600"><X className="w-3.5 h-3.5" /></button>}
        </div>
        <button data-testid="new-payment-btn" onClick={() => setShowModal(true)} className="zen-btn-primary"><Plus className="w-4 h-4" /> Yeni Tahsilat / Ödeme</button>
      </div>
      <div className="grid md:grid-cols-3 gap-4">
        <div className="zen-panel p-5"><div className="text-xs font-semibold text-slate-500 uppercase">Cari Bakiye (Müşteri Borcu)</div><div className="text-2xl font-extrabold mt-2 text-rose-500 zen-mono whitespace-nowrap">{fmt(totalReceivable, "TL")}</div><div className="text-[10px] text-slate-400 mt-1.5 leading-snug">Muhasebe DEFTERİ: onaylı siparişler + fatura/tahakkuklar − ödemeler. Müşterilerin şu an size borcu.</div></div>
        <div className="zen-panel p-5"><div className="text-xs font-semibold text-slate-500 uppercase">Açık Siparişlerden Kalan</div><div className="text-2xl font-extrabold mt-2 text-amber-500 zen-mono whitespace-nowrap">{fmt(pendingCollection, "TL")}</div><div className="text-[10px] text-slate-400 mt-1.5 leading-snug">Onaylı ama ÖDENMEMİŞ siparişlerin kalanı (KDV dahil). Teklif aşamasındakiler dahil değildir — iş henüz alınmadı sayılmaz.</div></div>
        <div className="zen-panel p-5"><div className="text-xs font-semibold text-slate-500 uppercase">Bugünkü Tahsilat</div><div className="text-2xl font-extrabold mt-2 text-emerald-500 zen-mono whitespace-nowrap">{fmt(payments.filter(p => p.date === new Date().toISOString().slice(0, 10) && (p.kind || "tahsilat") === "tahsilat").reduce((s, p) => s + p.amount, 0), "TL")}</div><div className="text-[10px] text-slate-400 mt-1.5 leading-snug">Bugün kasaya GİREN para. Para çıkışları (ödemeler) bu rakama dahil değildir.</div></div>
      </div>
      <div className="rounded-xl bg-sky-50/60 dark:bg-sky-950/20 border border-sky-200/70 dark:border-sky-900/40 px-4 py-2.5 text-[11px] text-slate-600 dark:text-slate-300 leading-relaxed">
        <b>Neden farklı rakamlar?</b> İlk iki kart aynı şeyi ölçmez: <b>Cari Bakiye</b> muhasebe defterini (onaylı işler + hareketler),
        <b>Açık Siparişlerden Kalan</b> ise yalnızca onaylı/üretilen işlerin ödenmemiş kısmını izler.
        <b>Teklif verildiğinde hiçbirine eklenmez</b> — iş siparişe dönüştüğünde cariye işlenir.
      </div>
      <div className="zen-panel overflow-hidden">
        <StickyScrollWrapper>
        <table className="zen-table">
          <thead><tr><th>Tarih</th><th>Cari</th><th>Sipariş</th><th>Yöntem</th><th className="text-right">Tutar</th><th>Not</th></tr></thead>
          <tbody>
            {pagedPayments.map((p) => {
              const c = customers.find((x) => x.id === p.customerId);
              return (
                <tr key={p.id}>
                  <td className="zen-mono text-xs">{p.date}</td>
                  <td className="font-semibold">
                    {c ? (
                      <button
                        data-testid={`acc-cust-link-${c.id}`}
                        onClick={() => setViewCust(c)}
                        className="text-left hover:text-[#E5322A] hover:underline transition-colors cursor-pointer"
                        title="Müşteri detayını aç"
                      >
                        {c.name}
                      </button>
                    ) : "-"}
                  </td>
                  <td className="zen-mono text-xs text-[#E5322A]">{p.orderId || "-"}</td>
                  <td><span className={`zen-chip whitespace-nowrap ${(p.kind || "tahsilat") === "tahsilat" ? "bg-emerald-500/15 text-emerald-600" : (p.kind === "tediye") ? "bg-rose-500/15 text-rose-500" : "bg-slate-500/15 text-slate-500"}`}>{(p.kind || "tahsilat") === "tahsilat" ? "Tahsilat" : p.kind === "tediye" ? "Ödeme" : p.kind === "borc_tahakkuku" ? "Borç Tah." : "Alacak Tah."}</span> <span className="zen-chip bg-slate-500/10">{p.method}</span></td>
                  <td className={`text-right zen-mono font-bold ${(p.kind || "tahsilat") === "tahsilat" ? "text-emerald-500" : p.kind === "tediye" ? "text-rose-500" : "text-slate-500"}`}>{(p.kind || "tahsilat") === "tahsilat" ? "+" : p.kind === "tediye" ? "−" : ""}{fmt(p.amount, p.currency)}</td>
                  <td className="text-xs text-slate-500">{p.note}</td>
                </tr>
              );
            })}
            {filteredPayments.length === 0 && (
              <tr><td colSpan={6} className="text-center text-slate-500 py-8">
                {q ? "Arama sonucu bulunamadı" : "Henüz tahsilat kaydı yok"}
              </td></tr>
            )}
          </tbody>
        </table>
        </StickyScrollWrapper>
        <ZenPagination total={filteredPayments.length} page={page} pageSize={pageSize} onPage={setPage} onPageSize={(s) => { setPageSize(s); setPage(1); }} />
      </div>
      {showModal && <PaymentForm customers={customers} orders={orders} onClose={() => setShowModal(false)} onSaved={load} />}
      {viewCust && <StatementDialog customer={viewCust} onClose={() => setViewCust(null)} />}
    </div>
  );
}
function PaymentForm({ customers, orders, onClose, onSaved }) {
  const [f, setF] = useState({ kind: "tahsilat", customerId: "", orderId: "", amount: 0, currency: "TL", method: "havale", note: "" });
  const custOrders = orders.filter(o => o.customerId === f.customerId && o.status !== "odendi");
  const submit = async () => {
    if (!f.customerId || !f.amount) { toast.error("Cari ve tutar gerekli"); return; }
    if (Number(f.amount) <= 0) { toast.error("Tutar sıfırdan büyük olmalı — yönü yukarıdan seçin"); return; }
    await api.post("/payments", f);
    const KIND_LABEL = { tahsilat: "Tahsilat", tediye: "Ödeme", borc_tahakkuku: "Borç tahakkuku", alacak_tahakkuku: "Alacak tahakkuku" };
    toast.success(`${KIND_LABEL[f.kind] || "İşlem"} kaydedildi`); onSaved(); onClose();
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
      <div className="zen-panel w-full max-w-lg p-6 zen-anim-up">
        <div className="flex justify-between items-start mb-4">
          <div>
            <h3 className="text-lg font-bold">Tahsilat / Ödeme Kaydı</h3>
            <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">Para hareketi veya belge kaydı eklemek içindir. Önce aşağıdan işlem türünü seçin.</p>
          </div>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <div className="space-y-3 text-sm">
          {/* İŞLEM TÜRÜ — kural: kullanıcı ne yaptığını ASLA tahmin etmek zorunda kalmasın */}
          <div className="rounded-xl bg-slate-50 dark:bg-[#131B2E] border border-slate-200 dark:border-[#22304A] p-3">
            <label className="block text-xs font-bold mb-1.5">Önce İşlem Türünü Seçin</label>
            <div className="grid grid-cols-2 gap-2">
              <button type="button" data-testid="pay-kind-tahsilat" onClick={() => setF({ ...f, kind: "tahsilat" })}
                className={`py-2 px-3 rounded-lg text-sm font-semibold border transition-colors ${f.kind === "tahsilat" ? "bg-emerald-500 text-white border-emerald-500" : "border-slate-300 dark:border-[#22304A] hover:bg-emerald-50 dark:hover:bg-emerald-500/10"}`}>
                ⬇ Tahsilat (para girişi)
              </button>
              <button type="button" data-testid="pay-kind-tediye" onClick={() => setF({ ...f, kind: "tediye" })}
                className={`py-2 px-3 rounded-lg text-sm font-semibold border transition-colors ${f.kind === "tediye" ? "bg-rose-500 text-white border-rose-500" : "border-slate-300 dark:border-[#22304A] hover:bg-rose-50 dark:hover:bg-rose-500/10"}`}>
                ⬆ Ödeme (para çıkışı)
              </button>
            </div>
            <label className="block text-[10px] font-semibold text-slate-500 dark:text-slate-400 mt-2.5 mb-1">Gelişmiş (belge kaydı — elden para hareketi YOK):</label>
            <select className="zen-input !text-xs" value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value })} data-testid="pay-kind-select">
              <option value="tahsilat">Tahsilat — müşteri size para ödedi (borcu azalır)</option>
              <option value="tediye">Ödeme (Tediye) — siz müşteriye para verdiniz (borcu artar)</option>
              <option value="borc_tahakkuku">Borç Tahakkuğu — fatura/belge kaydı, para çıkmaz (borcu artar)</option>
              <option value="alacak_tahakkuku">Alacak Tahakkuğu — fatura/belge kaydı, para girmez (borcu azalır)</option>
            </select>
            <p className="text-[10px] text-slate-500 dark:text-slate-400 mt-2 leading-relaxed">
              {f.kind === "tahsilat" && "✅ Müşteriden para ALDINIZ. Cari borcu azalır, para kasaya girer."}
              {f.kind === "tediye" && "↩️ Müşteriye para VERDİNİZ (iade, fazla ödeme vb.). Cari borcu artar, para kasadan çıkar."}
              {f.kind === "borc_tahakkuku" && "📄 Elden para ÇIKMAZ. Fatura/belge kaydı: müşterinin borcu artar (kalem işaretleme gibi)."}
              {f.kind === "alacak_tahakkuku" && "📄 Elden para GİRMEZ. Fatura/belge kaydı: borcu azalır (elden ödeme belgesi gibi)."}
            </p>
          </div>
          <div><label className="block text-xs font-semibold mb-1">Cari</label>
            <select data-testid="pay-customer" className="zen-input" value={f.customerId} onChange={(e) => setF({ ...f, customerId: e.target.value, currency: customers.find(c => c.id === e.target.value)?.currency || "TL" })}>
              <option value="">Seçiniz...</option>
              {customers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <p className="text-[10px] text-slate-400 mt-1">Para hareketinin kime ait olduğunu seçin.</p>
          </div>
          <div><label className="block text-xs font-semibold mb-1">Sipariş (ops.)</label>
            <select className="zen-input" value={f.orderId} onChange={(e) => setF({ ...f, orderId: e.target.value })}>
              <option value="">Genel Cari Tahsilat</option>
              {custOrders.map(o => <option key={o.id} value={o.id}>{ono(o)} - {fmt(o.total, o.currency)}</option>)}
            </select>
            <p className="text-[10px] text-slate-400 mt-1">Siparişe bağlarsanız o siparişin kalan borcundan düşer. "Genel Cari Tahsilat" = carinin toplam bakiyesine işlenir.</p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="block text-xs font-semibold mb-1">Tutar</label><input data-testid="pay-amount" type="number" className="zen-input" value={f.amount} onChange={(e) => setF({ ...f, amount: Number(e.target.value) })} /></div>
            <div><label className="block text-xs font-semibold mb-1">Para Birimi</label>
              <select className="zen-input" value={f.currency} onChange={(e) => setF({ ...f, currency: e.target.value })}><option>TL</option><option>EUR</option><option>USD</option></select>
            </div>
          </div>
          <p className="text-[10px] text-slate-400 -mt-1">Tutarı daima artı (+) girin — yönü yukarıdaki İşlem Türü belirler. Yabancı para seçerseniz TL karşılığı otomatik hesaplanır.</p>
          <div><label className="block text-xs font-semibold mb-1">Yöntem</label>
            <select className="zen-input" value={f.method} onChange={(e) => setF({ ...f, method: e.target.value })}><option>havale</option><option>nakit</option><option>kart</option><option>cek</option></select>
            <p className="text-[10px] text-slate-400 mt-1">Nakit = kasa, Havale/Kart = banka, Çek = vadelidir. Kasa-banka takibi için kullanılır.</p>
          </div>
          <div><label className="block text-xs font-semibold mb-1">Not</label><input className="zen-input" value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} /></div>
        </div>
        <div className="flex justify-end gap-2 mt-5"><button onClick={onClose} className="zen-btn-ghost">İptal</button><button data-testid="pay-save" onClick={submit} className="zen-btn-primary">Kaydet</button></div>
      </div>
    </div>
  );
}

// ============ INVENTORY MODULE ============
function InventoryModule() {
  const { tr } = useContext(UICtx);
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const load = async () => setRows((await api.get("/stock")).data);
  useEffect(() => { load(); }, []);
  const save = async (d) => {
    if (editing?.id) await api.put(`/stock/${editing.id}`, d); else await api.post("/stock", d);
    toast.success("Kaydedildi"); setEditing(null); load();
  };
  const del = async (s) => { if (!window.confirm(`"${s.name}" stok kaydı silinsin mi? Bu işlem geri alınamaz.`)) return; await api.delete(`/stock/${s.id}`); load(); };
  const filtered = rows.filter(r => (r.name + r.code).toLowerCase().includes(q.toLowerCase()));
  const pagedStock = filtered.slice((page - 1) * pageSize, page * pageSize);
  useEffect(() => { setPage(1); }, [q]);
  return (
    <div className="space-y-4 zen-anim-up">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-extrabold tracking-tight">{tr("Depo & Stok")}</h2>
        <div className="flex gap-2">
          <div className="flex items-center gap-2 zen-panel px-3"><Search className="w-4 h-4 text-slate-400" /><input value={q} onChange={(e) => setQ(e.target.value)} placeholder={tr("Ara...")} className="bg-transparent outline-none text-sm py-2 w-56" /></div>
          <button data-testid="new-stock-btn" onClick={() => setEditing({})} className="zen-btn-primary"><Plus className="w-4 h-4" />{tr("Yeni Malzeme")}</button>
        </div>
      </div>
      <div className="zen-panel overflow-hidden">
        <StickyScrollWrapper>
        <table className="zen-table">
          <thead><tr><th></th><th>{tr("Kod")}</th><th>{tr("Ürün")}</th><th>{tr("Kategori")}</th><th>{tr("Depo")}</th><th className="text-right">{tr("Miktar")}</th><th className="text-right">{tr("Min")}</th><th></th></tr></thead>
          <tbody>
            {pagedStock.map(s => {
              const critical = s.qty <= s.minLimit;
              return (
                <tr key={s.id}>
                  <td className="w-10">
                    {s.imageBase64
                      ? <img src={s.imageBase64} alt="" className="w-8 h-8 rounded object-cover border border-slate-200 dark:border-[#22304A]" />
                      : <div className="w-8 h-8 rounded bg-slate-100 dark:bg-[#1A2340] flex items-center justify-center"><ImageIcon className="w-4 h-4 text-slate-300" /></div>
                    }
                  </td>
                  <td className="zen-mono font-bold">{s.code}</td>
                  <td className="font-semibold">{s.name}<div className="text-[11px] text-slate-500">{s.supplier}</div></td>
                  <td className="text-xs">{s.category}</td>
                  <td className="text-xs">{s.warehouse}</td>
                  <td className={`text-right zen-mono font-bold ${critical ? "text-rose-500" : ""}`}>{s.qty} {s.unit}</td>
                  <td className="text-right zen-mono text-xs">{s.minLimit}</td>
                  <td>
                    <div className="flex gap-1 justify-end">
                      <button onClick={() => setEditing(s)} className="p-1.5 hover:bg-slate-100 dark:hover:bg-[#22304A] rounded"><Pencil className="w-4 h-4" /></button>
                      <button onClick={() => del(s)} className="p-1.5 hover:bg-rose-500/10 text-rose-500 rounded"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        </StickyScrollWrapper>
        <ZenPagination total={filtered.length} page={page} pageSize={pageSize} onPage={setPage} onPageSize={(s) => { setPageSize(s); setPage(1); }} />
      </div>
      {editing && <StockForm data={editing} onClose={() => setEditing(null)} onSave={save} />}
    </div>
  );
}
function StockForm({ data, onClose, onSave }) {
  const { tr } = useContext(UICtx);
  const [f, setF] = useState({ code: "", name: "", category: "Profil", subgroup: "", qty: 0, unit: "adet", minLimit: 0, warehouse: "Ana Depo", supplier: "", imageBase64: "", ...data });
  const [imgSearch, setImgSearch] = useState({ open: false, q: "", results: [], loading: false });
  const handleFile = (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    resizeImageToBase64(file, 400, 0.88).then(b64 => setF(prev => ({ ...prev, imageBase64: b64 })));
  };
  const searchImages = async () => {
    const q = imgSearch.q.trim();
    if (!q) { toast.error("Arama terimi girin"); return; }
    setImgSearch(s => ({ ...s, loading: true, results: [] }));
    try {
      const { data: res } = await api.get(`/images/search?q=${encodeURIComponent(q)}&num=8`);
      setImgSearch(s => ({ ...s, loading: false, results: res.results || [] }));
      if (!res.results?.length) toast.info("Sonuç bulunamadı");
    } catch (e) {
      setImgSearch(s => ({ ...s, loading: false }));
      toast.error(e?.response?.data?.detail || "Görsel arama başarısız");
    }
  };
  const fetchAndSetImage = async (url) => {
    try {
      toast.loading("Görsel yükleniyor...", { id: "fetch-stock-img" });
      const { data: res } = await api.get(`/images/fetch-url?url=${encodeURIComponent(url)}`);
      setF(prev => ({ ...prev, imageBase64: res.base64 }));
      setImgSearch(s => ({ ...s, open: false }));
      toast.success("Görsel seçildi", { id: "fetch-stock-img" });
    } catch {
      toast.error("Görsel alınamadı", { id: "fetch-stock-img" });
    }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
      <div className="zen-panel w-full max-w-xl p-6 zen-anim-up max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4"><h3 className="text-lg font-bold">{data?.id ? "Malzeme Düzenle" : "Yeni Malzeme"}</h3><button onClick={onClose}><X className="w-5 h-5" /></button></div>
        <div className="grid md:grid-cols-2 gap-3 text-sm">
          <div><label className="block text-xs font-semibold mb-1">{tr("Kod")}</label><input className="zen-input" value={f.code} onChange={(e) => setF({ ...f, code: e.target.value })} /></div>
          <div><label className="block text-xs font-semibold mb-1">{tr("Kategori")}</label><input className="zen-input" value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} /></div>
          <div><label className="block text-xs font-semibold mb-1">{tr("Alt Grup")}</label><input className="zen-input" placeholder={tr("örn. Dikey Zip")} value={f.subgroup || ""} onChange={(e) => setF({ ...f, subgroup: e.target.value })} /></div>
          <div className="md:col-span-2"><label className="block text-xs font-semibold mb-1">{tr("Ürün Adı")}</label><input className="zen-input" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></div>
          <div><label className="block text-xs font-semibold mb-1">{tr("Miktar")}</label><input type="number" className="zen-input" value={f.qty} onChange={(e) => setF({ ...f, qty: Number(e.target.value) })} /></div>
          <div><label className="block text-xs font-semibold mb-1">{tr("Birim")}</label><select className="zen-input" value={f.unit} onChange={(e) => setF({ ...f, unit: e.target.value })}><option>{tr("adet")}</option><option>m²</option><option>{tr("mtül")}</option><option>kg</option></select></div>
          <div><label className="block text-xs font-semibold mb-1">{tr("Min Limit")}</label><input type="number" className="zen-input" value={f.minLimit} onChange={(e) => setF({ ...f, minLimit: Number(e.target.value) })} /></div>
          <div><label className="block text-xs font-semibold mb-1">{tr("Depo")}</label><input className="zen-input" value={f.warehouse} onChange={(e) => setF({ ...f, warehouse: e.target.value })} /></div>
          <div className="md:col-span-2"><label className="block text-xs font-semibold mb-1">{tr("Tedarikçi")}</label><input className="zen-input" value={f.supplier} onChange={(e) => setF({ ...f, supplier: e.target.value })} /></div>
          <div className="md:col-span-2">
            <label className="block text-xs font-semibold mb-1">{tr("Görsel (opsiyonel)")}</label>
            {f.imageBase64 && (
              <div className="flex items-center gap-3 mb-2">
                <img src={f.imageBase64} alt="" className="w-16 h-16 rounded-lg object-cover border border-slate-200 dark:border-[#22304A]" />
                <button type="button" onClick={() => setF(prev => ({ ...prev, imageBase64: "" }))} className="text-xs text-rose-500 hover:underline">{tr("Görseli Kaldır")}</button>
              </div>
            )}
            <div className="flex gap-2">
              <input type="file" accept="image/*" onChange={handleFile} className="zen-input !py-1.5 flex-1" data-testid="stock-img-upload" />
              <button type="button"
                onClick={() => setImgSearch(s => ({ ...s, open: !s.open, q: s.q || f.name || f.code || "" }))}
                className="zen-btn-ghost !px-3 text-xs whitespace-nowrap flex items-center gap-1.5 shrink-0"
                data-testid="stock-img-search-btn">
                <Search className="w-3.5 h-3.5" /> {tr("Görsel Ara")}
              </button>
            </div>
            {imgSearch.open && (
              <div className="mt-2 p-3 bg-slate-50 dark:bg-[#0F1826] rounded-lg border border-slate-200 dark:border-[#22304A] space-y-2">
                <div className="flex gap-2">
                  <input className="zen-input flex-1 !py-1.5 text-xs" value={imgSearch.q}
                    onChange={e => setImgSearch(s => ({ ...s, q: e.target.value }))}
                    onKeyDown={e => e.key === "Enter" && searchImages()}
                    placeholder="Arama terimi (örn: alüminyum profil)..."
                    data-testid="stock-img-search-input" />
                  <button onClick={searchImages} disabled={imgSearch.loading}
                    className="zen-btn-primary !py-1.5 !px-3 text-xs flex items-center gap-1.5 shrink-0"
                    data-testid="stock-img-search-go">
                    {imgSearch.loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Search className="w-3.5 h-3.5" />} {tr("Ara")}
                  </button>
                </div>
                {imgSearch.results.length > 0 && (
                  <div className="grid grid-cols-4 gap-1.5" data-testid="stock-img-search-results">
                    {imgSearch.results.map((r, i) => (
                      <button key={i} onClick={() => fetchAndSetImage(r.link)} title={r.title}
                        className="aspect-square overflow-hidden rounded border border-slate-200 dark:border-[#22304A] hover:border-[#E5322A] hover:shadow-md transition-all"
                        data-testid={`stock-img-result-${i}`}>
                        <img src={r.thumbnail} alt={r.title} className="w-full h-full object-cover"
                          onError={e => { e.currentTarget.parentElement.style.opacity = "0.3"; }} />
                      </button>
                    ))}
                  </div>
                )}
                {imgSearch.results.length === 0 && !imgSearch.loading && (
                  <p className="text-[11px] text-slate-400 text-center py-1">{tr("Yukarıda arama yapın")}</p>
                )}
              </div>
            )}
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-5"><button onClick={onClose} className="zen-btn-ghost">{tr("İptal")}</button><button onClick={() => onSave(f)} className="zen-btn-primary">{tr("Kaydet")}</button></div>
      </div>
    </div>
  );
}

// ============ IMPORT MODULE (Excel/CSV/Yapistir) ============
function ImportModule() {
  const { tr } = useContext(UICtx);
  const [kind, setKind] = useState("customers");
  const [mode, setMode] = useState("file"); // file | paste
  const [rows, setRows] = useState([]);
  const [preview, setPreview] = useState([]);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pasteText, setPasteText] = useState("");

  const onFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const data = new Uint8Array(evt.target.result);
        const wb = XLSX.read(data, { type: "array" });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const json = XLSX.utils.sheet_to_json(ws, { defval: "" });
        setRows(json); setPreview(json.slice(0, 6));
        toast.success(`${json.length} satır okundu`);
      } catch (err) { toast.error("Dosya okunamadı: " + err.message); }
    };
    reader.readAsArrayBuffer(file);
  };

  const parsePaste = () => {
    if (!pasteText.trim()) { toast.error("Boş metin"); return; }
    // Split lines, detect tab or ; or ,
    const lines = pasteText.trim().split(/\r?\n/);
    const firstLine = lines[0];
    const delim = firstLine.includes("\t") ? "\t" : firstLine.includes(";") ? ";" : ",";
    const parseLine = (l) => l.split(delim).map(s => s.trim().replace(/^"|"$/g, ""));
    let headers = parseLine(firstLine);
    let dataLines = lines.slice(1);
    // Detect if first line is data (no headers) - heuristic: if first field is a number or looks like ID
    if (headers.every(h => /^[\d\s.,#]+$/.test(h) || h === "")) {
      headers = ["col_" + Math.random()].concat([]);  // fallback - shouldn't happen for bayiloji
    }
    const parsed = dataLines.filter(l => l.trim()).map((l) => {
      const values = parseLine(l);
      const obj = {};
      headers.forEach((h, i) => { obj[h] = values[i] || ""; });
      return obj;
    });
    setRows(parsed); setPreview(parsed.slice(0, 6));
    toast.success(`${parsed.length} satır ayrıştırıldı (${delim === "\t" ? "TAB" : delim} ayraç)`);
  };

  const upload = async () => {
    if (rows.length === 0) return;
    setLoading(true);
    try {
      const { data } = await api.post("/import", { kind, rows });
      setResult(data);
      toast.success(`${data.created} eklendi, ${data.updated} güncellendi`);
    } catch (e) { toast.error("Yükleme hatası"); }
    finally { setLoading(false); }
  };

  return (
    <div className="space-y-4 zen-anim-up">
      <div>
        <h2 className="text-2xl font-extrabold tracking-tight">{tr("Toplu İçeri Aktarım")}</h2>
        <p className="text-xs text-slate-500 mt-1">Excel dosyası yükle veya Bayiloji/Logo/Netsis tablosunu Ctrl+A/Ctrl+C ile kopyalayıp yapıştır.</p>
      </div>
      <div className="zen-panel p-6 space-y-4">
        <div>
          <label className="block text-xs font-semibold mb-2">Veri Türü</label>
          <div className="grid grid-cols-2 gap-2">
            <button onClick={() => setKind("customers")} className={`p-3 rounded-lg border-2 text-sm font-semibold ${kind === "customers" ? "border-[#E5322A] bg-[#E5322A]/10 text-[#E5322A]" : "border-slate-200 dark:border-[#22304A]"}`}>
              <Users className="w-5 h-5 mb-1 inline" /> Müşteriler (Cari)
            </button>
            <button onClick={() => setKind("prices")} className={`p-3 rounded-lg border-2 text-sm font-semibold ${kind === "prices" ? "border-[#E5322A] bg-[#E5322A]/10 text-[#E5322A]" : "border-slate-200 dark:border-[#22304A]"}`}>
              <Layers className="w-5 h-5 mb-1 inline" /> Fiyat Listesi
            </button>
          </div>
        </div>

        <div>
          <label className="block text-xs font-semibold mb-2">Aktarım Yolu</label>
          <div className="flex gap-2">
            <button onClick={() => setMode("file")} className={`flex-1 py-2 rounded-lg text-xs font-semibold border ${mode === "file" ? "bg-[#E5322A] text-white border-[#E5322A]" : "border-slate-200 dark:border-[#22304A]"}`}>📁 Excel Dosyası</button>
            <button onClick={() => setMode("paste")} className={`flex-1 py-2 rounded-lg text-xs font-semibold border ${mode === "paste" ? "bg-[#E5322A] text-white border-[#E5322A]" : "border-slate-200 dark:border-[#22304A]"}`}>📋 Yapıştır (Ctrl+V)</button>
          </div>
        </div>

        {mode === "file" && (
          <div className="p-6 border-2 border-dashed border-slate-300 dark:border-[#22304A] rounded-lg text-center">
            <Upload className="w-12 h-12 mx-auto text-slate-400 mb-2" />
            <p className="text-sm font-semibold mb-2">Excel dosyanı seç (.xlsx, .xls, .csv)</p>
            <input data-testid="import-file" type="file" accept=".xlsx,.xls,.csv" onChange={onFile} className="text-xs" />
          </div>
        )}

        {mode === "paste" && (
          <div>
            <div className="text-xs mb-2 space-y-1 p-3 rounded-lg bg-sky-500/5 border border-sky-500/20">
              <div className="font-bold">📋 Bayiloji'den nasıl kopyalarım?</div>
              <div>1. Bayiloji'de müşteri veya sipariş tablosunun olduğu sayfaya git</div>
              <div>2. Sayfada <strong>Ctrl + A</strong> ile hepsini seç, sonra <strong>Ctrl + C</strong></div>
              <div>3. Aşağıdaki kutuya <strong>Ctrl + V</strong> ile yapıştır</div>
              <div>4. "Ayrıştır" butonuna tıkla, önizlemeyi kontrol et, "İçeri Aktar"</div>
            </div>
            <textarea
              data-testid="paste-textarea"
              rows={10}
              className="zen-input w-full font-mono text-xs"
              placeholder="Bayiloji tablosunu buraya yapıştır..."
              value={pasteText}
              onChange={(e) => setPasteText(e.target.value)}
            />
            <button onClick={parsePaste} className="zen-btn-ghost mt-2 !text-xs">🔍 Ayrıştır</button>
          </div>
        )}

        {kind === "customers" && (
          <div className="p-3 bg-slate-50 dark:bg-[#131B2E] rounded-lg text-[10px]">
            <div className="font-bold mb-1">Desteklenen kolon adları:</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-1 text-slate-600 dark:text-slate-300">
              <div>Cari Adı / name</div><div>Cari Kodu / code</div>
              <div>Cari Grubu / grup</div><div>Şehir / adres</div>
              <div>Ülke / country</div><div>Para Birimi / currency</div>
              <div>Toplam Bakiye / balance</div><div>Telefon / phone</div>
            </div>
          </div>
        )}

        {preview.length > 0 && (
          <div>
            <div className="text-xs font-bold mb-2">Önizleme (ilk 6 satır - toplam {rows.length}):</div>
            <div className="overflow-x-auto zen-panel">
              <table className="text-xs">
                <thead><tr>{Object.keys(preview[0]).slice(0, 8).map(k => <th key={k} className="px-2 py-1 border-b border-slate-200 dark:border-[#22304A] font-bold">{k}</th>)}</tr></thead>
                <tbody>{preview.map((r, i) => <tr key={i}>{Object.keys(preview[0]).slice(0, 8).map(k => <td key={k} className="px-2 py-1 border-b border-slate-200 dark:border-[#22304A]">{String(r[k]).slice(0, 30)}</td>)}</tr>)}</tbody>
              </table>
            </div>
            <button onClick={upload} disabled={loading} data-testid="import-submit" className="zen-btn-primary mt-3">
              <Upload className="w-4 h-4" /> {loading ? "Yükleniyor..." : `${rows.length} kaydı İçeri Aktar`}
            </button>
          </div>
        )}

        {result && (
          <div className="p-4 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-sm">
            <div className="font-bold text-emerald-600">Yükleme Tamamlandı</div>
            <div>{result.created} yeni kayıt eklendi, {result.updated} kayıt güncellendi.</div>
            {result.totalErrors > 0 && <div className="text-rose-500 mt-2">⚠ {result.totalErrors} hata</div>}
          </div>
        )}
      </div>
    </div>
  );
}

// ============ PRODUCT GROUPS MODULE ============

export { AccountingModule, KasaBankaModule, CashAccountForm, TahsilatOdemeModule, PaymentForm, InventoryModule, StockForm, ImportModule };
export default AccountingModule;
